import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject, filter, map, mergeMap, takeUntil } from 'rxjs';
import {
  SIDEBAR_CONFIG,
  SidebarItem,
} from 'src/app/private/services/module.config';
import { PermissionService } from 'src/app/private/services/permission.service';
import { SubscriptionBillingLockService } from 'src/app/private/services/subscription-billing-lock.service';
import { TrackingService } from 'src/app/private/services/tracking.service';
import { SessionGuardService } from 'src/app/private/services/session-guard.service';
import { ResumeDataRefreshService } from 'src/app/private/services/resume-data-refresh.service';
import { UsersService } from 'src/app/private/services/users.service';
import { ThemeService } from 'src/app/private/services/theme.service';
import { FontScaleService } from 'src/app/private/services/font-scale.service';
import { AuthService } from 'src/app/public/services/auth.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { CacheInterceptor } from 'src/app/utils/cache.interceptor';

interface ModuleSearchResult {
  label: string;
  route: string;
  queryParams?: Record<string, string>;
  icon: string;
  group: string;
  path: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent {
  @Output() toggleMenuEvent = new EventEmitter<void>();
  @ViewChild('moduleSearchBox') moduleSearchBox?: ElementRef<HTMLElement>;
  @ViewChild('moduleSearchInput') moduleSearchInput?: ElementRef<HTMLInputElement>;

  @HostListener('window:keydown', ['$event'])
  onWindowKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.moduleSearchInput?.nativeElement?.focus();
    }
  }

  private destroy$: Subject<void> = new Subject<void>();
  private readonly billingOnlyNav: SidebarItem[] = [
    {
      key: 'billing_only',
      label: 'Billing & Subscription',
      icon: 'fas fa-file-invoice-dollar',
      module: 'billing_subscription',
      route: '/main/billing-subscription',
    },
  ];
  private allModules: ModuleSearchResult[] = [];

  moduleSearchQuery = '';
  showModuleSearchResults = false;
  activeSearchIndex = -1;
  userPictureOnly: boolean = false;
  profileName: any;
  logo: any;
  currentTheme = 'default';
  currentTitle: any;
  selectedCenter: any = '';
  centerList: any = [];
  spaciality: any = '';
  formAction = 'add';
  user: User = new User();
  isSubmitted: boolean = false;
  departmentsList: any;
  roles: any;
  centers: any;
  isLoading: boolean = false;
  isCenter: boolean = false;
  userMenu = [{ title: 'Profile' }, { title: 'Log out' }];
  signatures: any[] = [];
  selectedIndex: number | null = null;
  initLoading: boolean = true;

  isWhiteLabelOpen = false;

  constructor(
    private tracker: TrackingService,
    private userApi: UsersService,
    private localStorage: LocalStorageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private auth: AuthService,
    private modalService: NgbModal,
    private toast: ToastrService,
    private sessionGuard: SessionGuardService,
    private resumeDataRefresh: ResumeDataRefreshService,
    private themeService: ThemeService,
    private cache: CacheInterceptor,
    private permissionService: PermissionService,
    private billingLock: SubscriptionBillingLockService,
    public fontScale: FontScaleService,
  ) {
    this.profileName = localStorage.getProfileName();
    this.selectedCenter = localStorage.getCenterId();
    this.isCenter = localStorage.getIsCenter();
    this.spaciality = 'CRM';

    this.router.events
      .pipe(
        filter((ev) => ev instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route: any) => {
          let title = route.data['title'] || '';
          while (route.firstChild) {
            route = route.firstChild;
            title += route.data['title'];
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
      )
      .subscribe((event: any) => (this.currentTitle = event['title']));
  }

  get userAvatarUrl(): string | null {
    const u = this.user as any;
    return (
      u?.profile_photo ||
      u?.photo ||
      u?.profile_picture ||
      localStorage.getItem('profile_photo') ||
      localStorage.getItem('profilePhoto') ||
      localStorage.getItem('user_photo') ||
      null
    );
  }

  get filteredModuleResults(): ModuleSearchResult[] {
    const query = this.moduleSearchQuery.trim().toLowerCase();
    if (!query) return [];

    return this.allModules
      .filter(
        (item) =>
          item.label.toLowerCase().includes(query) ||
          item.path.toLowerCase().includes(query) ||
          item.group.toLowerCase().includes(query),
      )
      .slice(0, 8);
  }

  get userInitials(): string {
    const name = this.profileName || this.user?.fname || 'User';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0] ? parts[0].slice(0, 2).toUpperCase() : 'U';
  }

  get currentCenterName(): string {
    if (this.centerList && this.centerList.length > 0) {
      const match = this.centerList.find((c: any) => String(c.center_id) === String(this.selectedCenter));
      if (match && match.center_name) return match.center_name;
      if (this.centerList[0]?.center_name) return this.centerList[0].center_name;
    }
    return 'India';
  }

  get userRole(): string {
    const role = (this.localStorage.getRole() || '').trim();
    if (!role) return 'Administrator';
    if (role.toLowerCase() === 'admin') return 'Administrator';
    return role.charAt(0).toUpperCase() + role.slice(1);
  }

  get userEmail(): string {
    return (
      localStorage.getItem('emailId') ||
      localStorage.getItem('email') ||
      (this.user as any)?.email ||
      'chetan@gravitasai.com'
    );
  }

  public toggleMenu() {
    this.toggleMenuEvent.emit();
  }

  openWhiteLabelPanel(): void {
    this.isWhiteLabelOpen = true;
  }
  changeCenter(event: any) {
    let center_id = event.target.value;
    if (center_id) {
      localStorage.setItem('center_id', center_id);
      location.reload();
    }
  }
  ngOnInit() {
    // this.currentTitle = this.route.data['title'] ;
    this.logo =
      this.localStorage.getClientLogo() || 'assets/img/tina-right.png';
    this.permissionService.refresh();
    this.buildSearchableModules();

    // Keep list data fresh after laptop sleep / long background tabs
    this.resumeDataRefresh.start();

    // Subscribe to live logo changes from the white-label panel
    this.themeService.config$
      .pipe(takeUntil(this.destroy$))
      .subscribe((cfg) => {
        if (cfg.navbarLogoUrl) {
          this.logo = cfg.navbarLogoUrl;
        }
      });

    let param = {
      where: {
        client_id: this.localStorage.getClientId(),
      },
    };
    this.userApi.getAllCenter(param).subscribe({
      next: (res) => {
        if (res) {
          this.centerList = res;
          if (this.centerList.length === 1) {
            const defaultCenterId = this.centerList[0].center_id;
            this.selectedCenter = defaultCenterId;
            localStorage.setItem('center_id', defaultCenterId);
          } else if (this.centerList.length > 1) {
            const storedCenterId = this.localStorage.getCenterId();
            if (storedCenterId) {
              this.selectedCenter = storedCenterId;
            } else {
              const defaultCenterId = this.centerList[0].center_id;
              this.selectedCenter = defaultCenterId;
              localStorage.setItem('center_id', defaultCenterId);
            }
          } else {
            this.selectedCenter = this.localStorage.getCenterId();
          }
        }
      },
      error: (e) => {},
    });
  }

  resetSettings() {
    //reset Email Comminucations
    this.setcommincationPref();
  }

  setcommincationPref() {
    let payload: any = {
      client_id: this.localStorage.getClientId(),
      user_id: this.localStorage.getUserId(),
      commincation_pref: {},
    };
    this.userApi.updateUser(payload).subscribe({
      next: (res: any) => {
        this.toast.success('User communication preferences reset successfully');
        console.log('User commincation pref updated successfully:', res);
        this.localStorage.setCommincationPref('');
      },
      error: (err: any) => {},
    });
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeTheme(themeName: string) {}

  toggleSidebar(): boolean {
    return false;
  }

  navigateHome() {
    return false;
  }
  logout() {
    this.tracker.track('Logout', 'Authentication', 'User Logout', null);
    this.sessionGuard.stopPolling();
    this.resumeDataRefresh.stop();
    this.cache.clear(); // Clear all cached data on logout to prevent cross-user data leakage
    setTimeout(() => {
      this.auth.logOut();
      this.toast.success('User Logout Successfully');
      this.router.navigate(['login']);
    }, 1000);
  }

  saveUser(isValid: any) {
    this.isSubmitted = true;
    if (isValid) {
      this.isLoading = true;

      const { password, ...userWithoutPassword } = this.user;

      let user_sign: any[] = [];

      this.signatures.forEach((element, index) => {
        user_sign.push({
          signature: element,
          isActive: index === this.selectedIndex ? true : false,
        });
      });

      console.log('user_sign', user_sign);

      const payload = {
        ...userWithoutPassword,
        user_sign: user_sign,
      };

      this.userApi.updateUser(payload).subscribe({
        next: (res: any) => {
          this.getUser();
          this.modalService.dismissAll();
          localStorage.setItem('user_sign', JSON.stringify(user_sign));
          this.toast.success('User Updated Successfully', 'Success');
          this.isLoading = false;
        },
        error: (e: any) => {
          this.toast.error(e.error.msg, 'Error');
          this.isLoading = false;
        },
      });
    }
  }

  loadRoles() {
    let param = {
      where: {
        // center_id: parseInt(this.localStorage.getCenterId()),
        client_id: parseInt(this.localStorage.getClientId()),
      },
    };
    this.userApi.getAllRoles(param).subscribe({
      next: (res) => {
        console.log('role', res);
        this.roles = res;
      },
    });
  }
  getCenters() {
    let param = {
      where: {
        client_id: this.localStorage.getClientId(),
      },
    };
    this.userApi.getAllCenter(param).subscribe({
      next: (res) => {
        this.centers = res;
      },
    });
  }

  loadDepartments() {
    let param = {
      where: {
        // center_id:this.localStorage.getCenterId(),
        client_id: this.localStorage.getClientId(),
      },
    };
    this.userApi.getAllDepartment(param).subscribe({
      next: (res) => {
        this.departmentsList = res;
      },
    });
  }

  open(content: any) {
    this.getCenters();
    this.initLoading = true;
    // this.loadDepartments();
    // this.loadRoles();

    Promise.all([this.loadDepartments(), this.loadRoles()])
      .then(() => {
        this.getUser();
        this.user = new User();
        this.formAction = 'add';
        this.modalService.open(content, {
          size: 'xl',
          scrollable: true,
          centered: true,
          backdrop: 'static',
        });
      })
      .catch((error) => {
        console.error('Error loading departments or roles:', error);
      });

    // setTimeout(() => {
    //   this.getUser();
    //   this.user = new User();
    // }, 3000);
  }

  getUser() {
    const params = {
      user_id: this.localStorage.getUserId(),
    };

    this.userApi.getUser(params).subscribe({
      next: (res: any) => {
        this.initLoading = false;
        this.user = res;
        this.signatures = res.user_sign.map((sign: any) => sign.signature);
        this.selectedIndex = res.user_sign.findIndex(
          (sign: any) => sign.isActive,
        );
      },
      error: (e: any) => {
        this.initLoading = false;
        console.log(e);
      },
    });
  }

  openSignatureModal(content: any) {
    if (this.signatures.length >= 3) {
      this.toast.error('You can add only 3 signatures');
      return;
    }

    this.modalService.open(content, {
      size: 'lg',
      scrollable: true,
      centered: true,
    });
  }

  previewSignature(index: number) {
    this.selectedIndex = index;
  }

  selectSignature(index: number) {
    this.selectedIndex = index;
  }

  handleModalClose(event: boolean) {}

  updateSignatures(updatedSignatures: any) {
    console.log('updatedSIngature', updatedSignatures);
    this.signatures.push(updatedSignatures[0]);
  }

  deleteSignature(index: number) {
    this.signatures.splice(index, 1);
  }

  previewSignatureModal(content: any, selectedIndex: number | null) {
    this.selectedIndex = selectedIndex;
    this.modalService.open(content, {
      size: 'lg',
      scrollable: true,
      centered: true,
    });
  }

  showPassword: boolean = false;

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  onModuleSearchInput(): void {
    this.showModuleSearchResults = true;
    this.activeSearchIndex = this.filteredModuleResults.length ? 0 : -1;
  }

  onModuleSearchFocus(): void {
    if (this.moduleSearchQuery.trim()) {
      this.showModuleSearchResults = true;
    }
  }

  onModuleSearchKeydown(event: KeyboardEvent): void {
    const results = this.filteredModuleResults;
    if (!results.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeSearchIndex = Math.min(
        this.activeSearchIndex + 1,
        results.length - 1,
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeSearchIndex = Math.max(this.activeSearchIndex - 1, 0);
    } else if (event.key === 'Enter' && this.activeSearchIndex >= 0) {
      event.preventDefault();
      this.selectModule(results[this.activeSearchIndex]);
    } else if (event.key === 'Escape') {
      this.clearModuleSearch();
    }
  }

  selectModule(item: ModuleSearchResult): void {
    this.router.navigate([item.route], {
      queryParams: item.queryParams || {},
    });
    this.clearModuleSearch();
  }

  clearModuleSearch(): void {
    this.moduleSearchQuery = '';
    this.showModuleSearchResults = false;
    this.activeSearchIndex = -1;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (
      this.showModuleSearchResults &&
      this.moduleSearchBox?.nativeElement &&
      target &&
      !this.moduleSearchBox.nativeElement.contains(target)
    ) {
      this.showModuleSearchResults = false;
      this.activeSearchIndex = -1;
    }
  }

  private buildSearchableModules(): void {
    this.allModules = this.flattenModules(this.getRootNavItems());
  }

  private getRootNavItems(): SidebarItem[] {
    if (this.billingLock.isBillingRouteLockActive()) {
      return this.billingOnlyNav;
    }

    return SIDEBAR_CONFIG.filter((item) => this.canShowItem(item));
  }

  private flattenModules(
    items: SidebarItem[],
    inheritedModule?: string,
    parentLabel = '',
  ): ModuleSearchResult[] {
    const results: ModuleSearchResult[] = [];

    for (const item of items) {
      if (!this.canShowItem(item, inheritedModule)) continue;

      const nextInherited = item.module || inheritedModule;
      const currentPath = parentLabel
        ? `${parentLabel} › ${item.label}`
        : item.label;

      if (item.route) {
        results.push({
          label: item.label,
          route: item.route,
          queryParams: item.queryParams,
          icon: item.icon,
          group: parentLabel,
          path: currentPath,
        });
      }

      if (item.children?.length) {
        results.push(
          ...this.flattenModules(item.children, nextInherited, currentPath),
        );
      }
    }

    return results;
  }

  private canShowItem(item: SidebarItem, inheritedModule?: string): boolean {
    if (this.permissionService.bypassAllFeatures) return true;

    if (item.children?.length) {
      const nextInherited = item.module || inheritedModule;
      return (item.children || []).some((child) =>
        this.canShowItem(child, nextInherited),
      );
    }

    if (!item.module) {
      return inheritedModule
        ? this.permissionService.hasNavAccess(
            inheritedModule,
            item.permissionParents,
            item.allowParentModule,
          )
        : true;
    }

    return this.permissionService.hasNavAccess(
      item.module,
      item.permissionParents,
      item.allowParentModule,
    );
  }
}

export class User {
  user_id!: Number;
  prefix: string = '';
  fname: string = '';
  lname: string = '';
  email: string = '';
  password!: JSON;
  username: String = '';
  gender: String = '';
  picture: String = '';
  profile: String = '';
  twoFactorAuthentication: String = '';
  status: String = '';
  // client_id:String = "";
  client_id: Number = 0;
  center_id: Number = 0;
  dept_id: Number = 0;
  role_id: Number = 0;
}
